import type { Dispatch, SetStateAction } from "react";
import { getPromptInstructionsToRender } from "../../../api";
import { userInfo } from "../../../authConfig";
import { setSelectedProductInStore, setTemplateContentsInStore } from "../../../Store/TemplateContents";

export type TemplateContent = {
    fields?: Record<string, unknown>;
    [key: string]: unknown;
};

export type TemplateDetailsForQuestionEdit = {
    templateContent: TemplateContent | undefined;
    templateKeyWords: string | undefined;
};

export type ResolveTemplatePayloadResult = {
    templateId: string | undefined;
    templateContents: TemplateContent | undefined;
    templateKeys: string | undefined;
    promptInstructions: string;
    questionWithInstructions: string;
    questionContentForAI: string;
    hasTemplateContext: boolean;
};

export type TemplateResolutionResult = {
    templateEntry: any | null;
    templateId: string;
    isExplicitNone: boolean;
};

export const isValidTemplateId = (templateId?: string) => typeof templateId === "string" && templateId !== "" && templateId !== "None";
export const hasUsableTemplateEntry = (templateEntry: any): boolean => {
    const firstTemplateEntry = templateEntry?.template?.[0];
    return !!(
        templateEntry &&
        Array.isArray(templateEntry.template) &&
        templateEntry.template.length > 0 &&
        firstTemplateEntry &&
        typeof firstTemplateEntry === "object" &&
        !Array.isArray(firstTemplateEntry)
    );
};

export const extractPayloadError = (payload: any): string | undefined => {
    if (payload && typeof payload === "object" && !Array.isArray(payload) && payload.error) {
        return String(payload.error);
    }
    return undefined;
};

export const getPayloadUserFavourite = (payload: any): Record<string, any> | undefined => {
    if (
        payload &&
        typeof payload === "object" &&
        !Array.isArray(payload) &&
        payload.userFavourite &&
        typeof payload.userFavourite === "object" &&
        !Array.isArray(payload.userFavourite)
    ) {
        return payload.userFavourite;
    }
    return undefined;
};

export const hasTemplateReadFailure = (payload: any): boolean =>
    !!(payload && typeof payload === "object" && !Array.isArray(payload) && payload.templateReadFailed === true);

export const hasUserFavouriteReadFailure = (payload: any): boolean =>
    !!(payload && typeof payload === "object" && !Array.isArray(payload) && payload.userFavouriteReadFailed === true);

export const syncUserFavouriteFromPayload = (payload: any) => {
    const payloadUserFavourite = getPayloadUserFavourite(payload);

    if (hasUserFavouriteReadFailure(payload)) {
        if (!userInfo.userFavourite) {
            userInfo.userFavourite = {};
        }
        return;
    }

    if (payloadUserFavourite) {
        userInfo.userFavourite = payloadUserFavourite;
    } else if (!userInfo.userFavourite) {
        userInfo.userFavourite = {};
    }
};

export const parseTemplatePayload = async (response: any) => {
    if (response && typeof response.json === "function") {
        return await response.json();
    }
    return {};
};

export const getTemplateCollectionsFromPayload = (payload: any): Record<string, any[]> | null => {
    if (hasTemplateReadFailure(payload)) {
        return null;
    }

    const candidate = payload && typeof payload === "object" && !Array.isArray(payload) && payload.templates ? payload.templates : payload;

    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
        return null;
    }

    const hasTemplateCollections = Array.isArray(candidate.quickSearch) || Object.values(candidate).some(value => Array.isArray(value));

    return hasTemplateCollections ? (candidate as Record<string, any[]>) : null;
};

export const groupTemplateContentsByProduct = (templateContents: any[]): Record<string, any[]> => {
    const groupedContents: Record<string, any[]> = { quickSearch: Array.isArray(templateContents) ? templateContents : [] };

    (Array.isArray(templateContents) ? templateContents : []).forEach(templateContent => {
        const productKey = templateContent?.product || "Unknown";
        if (!groupedContents[productKey]) {
            groupedContents[productKey] = [];
        }
        groupedContents[productKey].push(templateContent);
    });

    return groupedContents;
};

export const getTemplateContentsFromPayload = (payload: any): any[] => {
    if (Array.isArray(payload)) {
        return payload.filter((templateEntry: any) => hasUsableTemplateEntry(templateEntry));
    }

    const candidate = payload && typeof payload === "object" && !Array.isArray(payload) && payload.templates ? payload.templates : payload;

    if (Array.isArray(candidate)) {
        return candidate.filter((templateEntry: any) => hasUsableTemplateEntry(templateEntry));
    }

    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
        const groupedTemplateContents = Object.entries(candidate)
            .filter(([key, value]) => key !== "quickSearch" && Array.isArray(value))
            .flatMap(([, value]) => value as any[]);

        if (groupedTemplateContents.length > 0) {
            return groupedTemplateContents.filter((templateEntry: any) => hasUsableTemplateEntry(templateEntry));
        }

        if (Array.isArray(candidate.quickSearch)) {
            return candidate.quickSearch.filter((templateEntry: any) => hasUsableTemplateEntry(templateEntry));
        }
    }

    return [];
};

export const syncTemplatePayloadToStore = (payload: any, activeProduct: string, dispatch: any) => {
    syncUserFavouriteFromPayload(payload);
    const nextTemplateContents = getTemplateContentsFromPayload(payload);

    dispatch(setTemplateContentsInStore(nextTemplateContents));
    dispatch(setSelectedProductInStore(activeProduct));

    return nextTemplateContents;
};

export const applyTemplatePayloadState = (
    payload: any,
    activeProduct: string,
    dispatch: any,
    setTemplateContents: Dispatch<SetStateAction<any>>,
    setTemplateProduct: Dispatch<SetStateAction<string>>
) => {
    const nextTemplateContents = getTemplateCollectionsFromPayload(payload);

    if (!nextTemplateContents) {
        return null;
    }

    syncTemplatePayloadToStore(payload, activeProduct, dispatch);
    setTemplateContents(nextTemplateContents);
    setTemplateProduct(activeProduct);

    return nextTemplateContents;
};

export const getTemplateEntryById = (templateIdValue: string | undefined, templateCollections: any): any | undefined => {
    if (!isValidTemplateId(templateIdValue)) {
        return undefined;
    }

    const templates = Array.isArray(templateCollections)
        ? templateCollections
        : Object.values(templateCollections || {})
              .filter(Array.isArray)
              .flatMap(value => value as any[]);

    return templates.find((tempContent: any) => tempContent.id === templateIdValue && hasUsableTemplateEntry(tempContent));
};

export const getTemplatePromptTextValue = (templateContentData: any): string => {
    if (!!(templateContentData && templateContentData !== "")) {
        const normalizePromptText = (value: string): string => String(value ?? "").replace(/[ \t]+$/gm, "");
        const templatePreviewPrompt: string = normalizePromptText(templateContentData?.previewPrompt ?? "");
        const renderedPromptInstructions: string = normalizePromptText(getPromptInstructionsToRender(templateContentData));
        const rawPromptInstructions: string = normalizePromptText(templateContentData?.promptInstructions ?? "");
        const fallbackPrompt: string = normalizePromptText(templateContentData?.prompt ?? "");

        for (const instructionsText of [renderedPromptInstructions, rawPromptInstructions]) {
            if (templatePreviewPrompt && instructionsText && templatePreviewPrompt.endsWith(instructionsText)) {
                const templatePrompt = templatePreviewPrompt.slice(0, templatePreviewPrompt.length - instructionsText.length).replace(/[ \t]+$/gm, "");

                if (templatePrompt) {
                    return templatePrompt;
                }
            }
        }

        if (
            fallbackPrompt &&
            (!templatePreviewPrompt ||
                templatePreviewPrompt === rawPromptInstructions ||
                templatePreviewPrompt === renderedPromptInstructions ||
                templatePreviewPrompt.startsWith(fallbackPrompt))
        ) {
            return fallbackPrompt;
        }

        return templatePreviewPrompt || fallbackPrompt;
    }
    return "";
};

export const getTemplateDataById = (templateIdValue: string | undefined, templateContents: any[]): TemplateContent | undefined => {
    return getTemplateEntryById(templateIdValue, templateContents)?.template?.[0];
};

export const getPromptInstructionsFromQuestion = (questionValue: string, extractedQuestionValue: string): string => {
    if (!questionValue || !extractedQuestionValue) {
        return "";
    }

    if (questionValue.startsWith(extractedQuestionValue)) {
        const suffix = questionValue.slice(extractedQuestionValue.length);
        return suffix.trim() === "" ? "" : suffix;
    }

    const promptTag = "<PromptInstructions>";
    const promptTagIndex = questionValue.indexOf(promptTag);
    return promptTagIndex >= 0 ? questionValue.slice(promptTagIndex) : "";
};

/**
 * Gets template content for AI payload
 */
export const getTemplateContentForPayload = (templateIdValue: string, question: string, templateContents: any[]): string => {
    const filteredTemplateValue = getTemplateDataById(templateIdValue, templateContents);

    const payloadQuestionForAI = question.trim() + getPromptInstructionsToRender(filteredTemplateValue);
    return payloadQuestionForAI;
};

/**
 * Reforms question content for AI with template instructions
 */
export const reformQuestionContentForAI = (
    extractedQuestion: string,
    templateIdValue: string | undefined,
    templateContents: any[],
    promptInstructionsValue?: string,
    templateContentValue?: any
): string => {
    const promptInstructionsText: string =
        promptInstructionsValue ?? getPromptInstructionsToRender(templateContentValue ?? getTemplateDataById(templateIdValue, templateContents));
    const trimmedInstructions = promptInstructionsText?.trim();

    if (!trimmedInstructions) {
        return extractedQuestion;
    }

    const hasPromptInstructionsTags = /<\s*PromptInstructions\b[^>]*>/i.test(trimmedInstructions) && /<\/\s*PromptInstructions\s*>/i.test(trimmedInstructions);
    const instructionsSegment = hasPromptInstructionsTags ? trimmedInstructions : `<PromptInstructions>${trimmedInstructions}</PromptInstructions>`;

    return extractedQuestion + "\n\n" + instructionsSegment;
};

/**
 * Gets template details for question edit
 */
export const getTemplateDetailsForQuestionEdit = (templateIdValue: string, templateContents: any[]): TemplateDetailsForQuestionEdit => {
    const filteredTemplateValue = getTemplateDataById(templateIdValue, templateContents);

    const keyvalues = filteredTemplateValue ? Object.values(filteredTemplateValue?.fields ?? {}).join(" ") : undefined;

    return { templateContent: filteredTemplateValue, templateKeyWords: keyvalues };
};

export const resolveTemplatePayload = (
    questionValue: string,
    extractedQuestionValue: string,
    templateContents: any[],
    templateIdValue?: string,
    templateContentCandidate?: any,
    fallbackTemplateKeys?: string,
    validateTemplateId?: (templateId?: string) => boolean
): ResolveTemplatePayloadResult => {
    const isTemplateIdValid = validateTemplateId ?? isValidTemplateId;
    const hasValidTemplateId = isTemplateIdValid(templateIdValue);
    const templateFromId: TemplateContent | undefined =
        hasValidTemplateId && templateIdValue ? templateContents.find((tempContent: any) => tempContent.id === templateIdValue)?.template?.[0] : undefined;
    const templateContentValue: TemplateContent | undefined =
        templateFromId ??
        (hasValidTemplateId &&
        !!templateContentCandidate &&
        templateContentCandidate !== "" &&
        typeof templateContentCandidate === "object" &&
        !Array.isArray(templateContentCandidate)
            ? (templateContentCandidate as TemplateContent)
            : undefined);
    const promptInstructionsFromQuestion = getPromptInstructionsFromQuestion(questionValue, extractedQuestionValue);
    const promptInstructionsFromTemplate = templateContentValue ? getPromptInstructionsToRender(templateContentValue) : "";
    const promptInstructions = promptInstructionsFromQuestion !== "" ? promptInstructionsFromQuestion : promptInstructionsFromTemplate;
    const normalizedTemplateId = hasValidTemplateId && !!templateContentValue ? templateIdValue : undefined;
    const templateKeysFromContent = templateContentValue ? Object.values(templateContentValue?.fields ?? {}).join(" ") : undefined;
    const questionWithInstructions = promptInstructions !== "" ? extractedQuestionValue + promptInstructions : extractedQuestionValue;
    const questionContentForAI = promptInstructions?.trim()
        ? reformQuestionContentForAI(extractedQuestionValue, normalizedTemplateId, templateContents, promptInstructions, templateContentValue)
        : extractedQuestionValue;

    return {
        templateId: normalizedTemplateId,
        templateContents: templateContentValue,
        templateKeys: templateKeysFromContent ?? fallbackTemplateKeys,
        promptInstructions,
        questionWithInstructions,
        questionContentForAI,
        hasTemplateContext: !!(normalizedTemplateId || promptInstructions?.trim())
    };
};

/**
 * Gets user default content template
 */
export const getUserDefaultContentTemplate = (selectedProduct: string, userFavourite: any, templateContents: any[]): any => {
    const selectedProductId = userFavourite?.[selectedProduct] ?? "";
    return getTemplateEntryById(selectedProductId, templateContents) || "";
};

/**
 * Gets system default template
 */
export const getSystemDefaultTemplate = (selectedProduct: string, templateContents: any[]): any => {
    const templates = Array.isArray(templateContents) ? templateContents : [];
    const exactProductDefault =
        templates.find((tempContent: any) => tempContent.product === selectedProduct && tempContent.template?.[0]?.isDefault === true) || "";
    return exactProductDefault;
};

export const resolveEffectiveTemplateForProduct = (
    selectedProduct: string,
    userFavourite: Record<string, any> | undefined,
    templateContents: any[]
): TemplateResolutionResult => {
    const templates = Array.isArray(templateContents) ? templateContents : [];
    const selectedProductHasTemplates = templates.some((tempContent: any) => tempContent?.product === selectedProduct && hasUsableTemplateEntry(tempContent));

    if (selectedProduct !== "ALL" && !selectedProductHasTemplates) {
        const allProductFavouriteTemplateId = userFavourite?.["ALL"];

        if (allProductFavouriteTemplateId === "None") {
            return {
                templateEntry: null,
                templateId: "",
                isExplicitNone: true
            };
        }

        if (allProductFavouriteTemplateId === "" || allProductFavouriteTemplateId === null || allProductFavouriteTemplateId === undefined) {
            return {
                templateEntry: null,
                templateId: "",
                isExplicitNone: false
            };
        }

        const allProductFavouriteTemplate = isValidTemplateId(allProductFavouriteTemplateId)
            ? getTemplateEntryById(allProductFavouriteTemplateId, templateContents) || null
            : null;

        return {
            templateEntry: allProductFavouriteTemplate,
            templateId: allProductFavouriteTemplate?.id ?? "",
            isExplicitNone: false
        };
    }

    const favouriteTemplateId = userFavourite?.[selectedProduct];

    if (favouriteTemplateId === "None") {
        return {
            templateEntry: null,
            templateId: "",
            isExplicitNone: true
        };
    }

    if (favouriteTemplateId === "" || favouriteTemplateId === null || favouriteTemplateId === undefined) {
        return {
            templateEntry: null,
            templateId: "",
            isExplicitNone: false
        };
    }

    if (isValidTemplateId(favouriteTemplateId)) {
        const favouriteTemplate = getTemplateEntryById(favouriteTemplateId, templateContents) || null;
        if (favouriteTemplate) {
            return {
                templateEntry: favouriteTemplate,
                templateId: favouriteTemplate.id,
                isExplicitNone: false
            };
        }
    }

    const systemDefaultTemplate = getSystemDefaultTemplate(selectedProduct, templateContents) || null;

    return {
        templateEntry: systemDefaultTemplate,
        templateId: systemDefaultTemplate?.id ?? "",
        isExplicitNone: false
    };
};
